// src/pages/MultiBucketAnalyticsDashboard/CombinedAnalyticsDashboard.js
//
// "Combined Analysis" view for the multi-bucket page.
//
// Every one of the 16 wired bucket-analytics endpoints (see api.js keys
// cc_bucketAnalytics_multi_*) is fetched ONCE for all selected buckets
// together (comma-joined codes), then merged HERE, per widget, independently:
//
//   - a widget is merged from whichever buckets actually returned
//     success === true AND non-empty data for THAT widget
//   - a bucket missing/failing on widget A does not block it from being
//     included in widget B if it succeeded there — each section is combined
//     on its own
//   - if only 2 of 5 selected buckets have sales-volume data, the sales
//     volume chart below combines those 2 (no error, nothing thrown)
//   - if 4 of 5 have no data at all for a section, that section combines
//     the 1 that does — same "no data" placeholder as the single-bucket
//     page only shows once NONE of the selected buckets have anything
//
// This file does not own the DashboardLayout/BreadCrumb/bucket-chip-bar —
// MultiBucketAnalyticsDashboard.js renders those and drops this component
// in underneath when the person picks "Combined Analysis".

import React, { useEffect, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import { styled } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import { AiOutlineReload } from 'react-icons/ai';
import { BiInfoCircle } from 'react-icons/bi';
import XLSX from 'sheetjs-style';
import * as FileSaver from 'file-saver';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';
import { ButtonDropdown } from 'elements';
import { DateRangePicker } from 'mui-daterange-picker';

import { ErrorBlock } from 'components';
import DashboardCard from 'components/DashboardCard';
import BarChart from 'components/eCharts/BarChart';
import PieChart from 'components/eCharts/PieChart';
import AreaChart from 'components/eCharts/AreaChart';
import FunnelChart from 'components/eCharts/FunnelChart';

// exact same styling rules the single-bucket page uses, so section titles,
// spacing, "no data" text, etc. all look identical
import styles from 'pages/AnalyticDashboard/styles';
import { getCCMultiBucketAnalytics } from 'redux/actions/analytics/analyticsActions';
import MultiBucketAnalyticsExporter from './MultiBucketAnalyticsExporter';

const useStyles = makeStyles(styles);

const BUCKET_COLORS = ['#C8102E', '#36333d', '#a2aaad', '#f39c12', '#2c7fb8'];
const POS_TYPE_COLORS = ['#a2aaad', '#C8102E', '#36333d', '#982639', '#FF2C4F'];

// identical orange range-slider styling the single-bucket page uses under
// Sales Volume / Annual Sales Trend / Colorant Consumption
const DATA_ZOOM_CONFIG = [
  { type: 'inside', start: 0, end: 100 },
  {
    start: 0,
    end: 100,
    borderColor: '#FFB1A8',
    backgroundColor: '#FFB1A8',
    height: 30,
    moveHandleStyle: { color: '#C8102E', opacity: 1 },
    dataBackground: {
      lineStyle: { color: '#C8102E', width: 0.5 },
      areaStyle: { color: '#C8102E', width: 0.5 },
    },
    selectedDataBackground: {
      lineStyle: { color: '#C8102E', width: 0.5 },
      areaStyle: { color: '#C8102E', width: 0.5 },
    },
    emphasis: { moveHandleStyle: { color: '#C8102E', opacity: 0.5 } },
  },
];

// same xlsx export used everywhere on the single-bucket page (Dashboard.js
// exportToExcel) — identical file type/extension so downloads behave the same
const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=UTF-8';
const fileExtension = '.xlsx';
const exportToExcel = async (excelData, filename) => {
  const ws = XLSX.utils.json_to_sheet(excelData && excelData.length ? excelData : [{}]);
  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: fileType });
  FileSaver.saveAs(blob, filename + fileExtension);
};

/* ---------------------------------------------------------------------- */
/* Same styled shells as the single-bucket page                            */
/* ---------------------------------------------------------------------- */

const FirstSection = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  height: 'auto',
  display: '-webkit-box',
}));

const RootContainer = styled('div')(() => ({
  position: 'relative',
  width: '50%',
  display: '-webkit-box',
}));

const MachineStatistics = styled('div')(() => ({
  position: 'relative',
  width: '50%',
  display: '-webkit-box',
}));

const WhiteCard = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  minHeight: 500,
  height: 'auto',
  background: '#FFFFFF 0% 0% no-repeat padding-box',
  boxShadow: '0px 2px 10px #00000029',
  borderRadius: 5,
  padding: '2% 3% 2% 3%',
  marginBottom: '2%',
}));

const Refresh = styled('div')(({ theme }) => ({
  background: '#c8102e',
  borderRadius: 5,
  flex: 'none',
  fontFamily: 'Roboto',
  cursor: 'pointer',
  fontSize: 12,
  width: 'fit-content',
  color: '#fff',
  marginRight: '10px',
  '&:hover': {
    background: '#eb314f',
    boxShadow: '0 8px 16px 0 rgba(0,0,0,0.2)',
  },
  [theme.breakpoints.up('xl')]: { fontSize: 14 },
}));

// identical download-button styling to Dashboard.js's ChartButton
const ChartButton = styled('span')(({ theme, clickDisable }) => ({
  background: '#c8102e',
  padding: 10,
  borderRadius: 5,
  fontFamily: 'Roboto',
  cursor: 'pointer',
  color: '#fff',
  fontSize: 12,
  marginLeft: '2%',
  pointerEvents: clickDisable ? 'none' : 'all',
  '&:hover': {
    background: '#eb314f',
    boxShadow: '0 8px 16px 0 rgba(0,0,0,0.2)',
  },
}));

// Robust "loading / no data" placeholder that reserves real height instead of
// the single-bucket page's `classes.loaderDiv`, which centers itself with a
// `translateY(460%)` transform tuned for that page's own fixed-height cards.
// Reused as-is inside our nested Top Sales Statistics sub-sections, that
// transform doesn't reserve any space, so stacked "No data found" messages
// collapsed on top of each other instead of each owning its section's height.
const SectionPlaceholder = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 300,
  alignItems: 'center',
  justifyContent: 'center',
}));
const NoDataText = styled('p')(() => ({
  fontSize: 20,
  font: 'normal normal normal 30px Roboto',
}));

// Ranked charts (Top Colors, Top Products, Colorant Consumption Bar, Top
// Can Sales, Location Based Sales — anything built with mergeTopList /
// mergeTopListMultiSum) merge every selected bucket's contribution into one
// combined ranking, so unlike the line charts there's no per-bucket line to
// toggle. This renders every selected bucket as a small chip ALWAYS — not
// just when the chart is empty — colored/enabled if that bucket actually
// contributed data to the current ranking, greyed/disabled if it didn't.
// Same enable/disable-by-bucket-id feel as the line charts' legend, adapted
// to a chart type that combines rather than lines.
function BucketChipStrip({ codeList, hasDataByCode }) {
  if (!codeList || codeList.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
      {codeList.map((code) => {
        const active = !!hasDataByCode?.[code];
        return (
          <span
            key={code}
            title={active ? undefined : 'No data for this bucket in the current range'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px',
              borderRadius: 12,
              border: `1px solid ${active ? '#C8102E' : '#ddd'}`,
              fontSize: 12,
              color: active ? '#C8102E' : '#aaa',
              background: active ? '#FCEBEB' : '#fafafa',
            }}
          >
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#C8102E' : '#ccc' }} />
            {code}
          </span>
        );
      })}
    </div>
  );
}

// Ranked/merged charts (Top Colors, Top Products, Colorant Consumption Bar,
// etc — anything built with mergeTopList) have no per-bucket line to fall
// back to a flat 0 for, unlike the time-series charts mergeSeriesByBucket
// fixes. But "No data found" alone still hides WHICH buckets were actually
// selected and checked. This shows every selected bucket as a small "0"
// chip underneath the no-data message, so the selection is always visible
// even when there's genuinely nothing to rank.
function BucketStatusStrip({ codeList }) {
  if (!codeList || codeList.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 12 }}>
      {codeList.map((code) => (
        <span
          key={code}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 12,
            border: '1px solid #ddd',
            fontSize: 12,
            color: '#888',
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ccc' }} />
          {code}: 0
        </span>
      ))}
    </div>
  );
}

// small reusable wrapper so every section's download button is one line
function DownloadButton({ data, filename, disabled, t }) {
  const classes = useStyles();
  return (
    <Tooltip title={t('XdataAnalyticsDetail.download')}>
      <ChartButton
        clickDisable={disabled}
        onClick={() => exportToExcel(data, filename)}
        style={{ height: 32, paddingTop: 5, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}
      >
        <DownloadIcon className={classes.DownloadIcon} />
      </ChartButton>
    </Tooltip>
  );
}

/* ---------------------------------------------------------------------- */

// ISO (yyyy-mm-dd) for API params — same format the single-bucket page sends
function fmtISO(d) {
  const date = d instanceof Date ? d : new Date(d);
  const pad = (n) => (n < 10 ? '0' + n : '' + n);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
// dd/mm/yyyy for the calendar button's label — same format the single-bucket
// page's ChartButton shows next to the CalendarIcon
function fmtDisplay(d) {
  const date = d instanceof Date ? d : new Date(d);
  const pad = (n) => (n < 10 ? '0' + n : '' + n);
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
}
// maps the ButtonDropdown's translated label back to the date_type value the
// API expects — same mapping the single-bucket page's *SelectOptionChange
// handlers use (t('...thismonth') -> "thismonth", etc.)
function dateTypeFromLabel(t, option) {
  if (option === t('XdataAnalyticsDetail.thismonth')) return 'thismonth';
  if (option === t('XdataAnalyticsDetail.thisyear')) return 'thisyear';
  if (option === t('XdataAnalyticsDetail.lastmonth')) return 'lastmonth';
  if (option === t('XdataAnalyticsDetail.currentQuarter')) return 'thisquarter';
  if (option === t('XdataAnalyticsDetail.prevQuarter')) return 'lastquarter';
  return 'lastyear';
}
// Inverse of dateTypeFromLabel. Every dropdown below tracks BOTH a date_type
// key (used in the API call) and a display label (shown on the button) as
// two separate pieces of state. If the label state is ever left empty, the
// ButtonDropdown component falls back to whatever `label` prop was passed —
// which can say something different than the date_type actually being
// queried. Deriving the label from the same key with this function keeps
// them permanently in sync, so the button never displays a range that isn't
// the one actually being fetched.
function labelForDateType(t, key) {
  if (key === 'thismonth') return t('XdataAnalyticsDetail.thismonth');
  if (key === 'thisyear') return t('XdataAnalyticsDetail.thisyear');
  if (key === 'lastmonth') return t('XdataAnalyticsDetail.lastmonth');
  if (key === 'thisquarter') return t('XdataAnalyticsDetail.currentQuarter');
  if (key === 'lastquarter') return t('XdataAnalyticsDetail.prevQuarter');
  return t('XdataAnalyticsDetail.lastyear');
}

/* ---------------------------------------------------------------------- */
/* Generic per-widget aggregation helpers — the "skip what's missing,     */
/* combine what's there" rule lives here, once, reused by every section.  */
/* ---------------------------------------------------------------------- */

// A bucket's per-widget entry is supposed to look like { success: true, data }
// but tolerate the couple of other shapes the backend might use for a given
// endpoint (status: 'success' instead of success: true, or no explicit flag
// at all when data is just present) — being strict here was the actual cause
// of buckets that DO have data (e.g. Top Can Sales) being silently dropped
// from the combined charts.
function bucketSucceeded(entry) {
  if (!entry) return false;
  if (entry.success === true) return true;
  if (entry.status === 'success') return true;
  if (entry.success === undefined && entry.status === undefined && entry.data != null) return true;
  return false;
}

// Pulls the actual data payload out of a bucket entry, tolerating either
// {success, data} or {status, data} or the raw payload itself.
function bucketData(entry) {
  return entry?.data !== undefined ? entry.data : entry;
}

// { buckets, anySuccess } for one widget's redux slice
function useWidget(ccMultiBucketData, widget, codeList) {
  return useMemo(() => {
    const buckets = ccMultiBucketData?.[widget]?.data?.buckets || {};
    let anySuccess = false;
    codeList.forEach((c) => {
      if (bucketSucceeded(buckets?.[c])) anySuccess = true;
    });
    return { buckets, anySuccess };
  }, [ccMultiBucketData, widget, codeList]);
}

// sum a numeric field across every bucket that succeeded AND has that field
function sumField(buckets, codeList, accessor) {
  let total = 0;
  let found = false;
  codeList.forEach((code) => {
    const entry = buckets?.[code];
    if (!bucketSucceeded(entry)) return; // skip, don't throw
    const v = accessor(bucketData(entry));
    if (v === undefined || v === null) return;
    found = true;
    total += Number(v) || 0;
  });
  return { total, found };
}

// merge "top N" category lists (colors/products/etc.) across buckets by
// name, summing their value, then re-sorting and slicing to `limit`.
// Also tracks each contributing bucket's own value under `bucket_<code>` so
// downloads can show a per-bucket-id breakdown next to the combined total —
// same idea as the single-bucket page's own export, just with one extra
// column per selected bucket.
function mergeTopList(buckets, codeList, listAccessor, nameKey, valueKey, limit = 10, mode = 'top') {
  const map = new Map();
  const colorMap = new Map();
  const perBucket = new Map(); // name -> { code: value }
  const bucketHasData = {}; // code -> true if THIS bucket contributed at least one row
  let found = false;
  codeList.forEach((code) => {
    bucketHasData[code] = false;
    const entry = buckets?.[code];
    if (!bucketSucceeded(entry)) return;
    const d = bucketData(entry);
    let rows = listAccessor(d);
    // defensive fallback: if the expected nested path wasn't an array but
    // the bucket's raw data IS an array, use that instead of dropping it
    if (!Array.isArray(rows) && Array.isArray(d)) rows = d;
    if (!Array.isArray(rows) || rows.length === 0) return;
    found = true;
    bucketHasData[code] = true;
    rows.forEach((row) => {
      const name = row?.[nameKey];
      if (!name) return;
      const val = Number(row?.[valueKey] || 0);
      map.set(name, (map.get(name) || 0) + val);
      const bucketVals = perBucket.get(name) || {};
      bucketVals[code] = (bucketVals[code] || 0) + val;
      perBucket.set(name, bucketVals);
      if (row?.hex_color && !colorMap.has(name)) colorMap.set(name, row.hex_color);
    });
  });
  const merged = Array.from(map.entries())
    .map(([name, value]) => {
      const bucketVals = perBucket.get(name) || {};
      const bucketCols = {};
      codeList.forEach((code) => { bucketCols[`bucket_${code}`] = bucketVals[code] || 0; });
      return { [nameKey]: name, ...bucketCols, [valueKey]: value, hex_color: colorMap.get(name) };
    })
    .sort((a, b) => (mode === 'worst' ? a[valueKey] - b[valueKey] : b[valueKey] - a[valueKey]))
    .slice(0, limit);
  return { rows: merged, found, bucketHasData };
}

// same as mergeTopList, but sums SEVERAL value keys per row instead of one —
// needed for Location Based Sales, where the single-bucket page renders two
// bars per POS (previous quarter + current quarter) from the same row.
// Rows are sorted/limited by the FIRST key in valueKeys (matches how the
// single-bucket page treats its "top_ten"/"worst_ten" lists as pre-ranked).
function mergeTopListMultiSum(buckets, codeList, listAccessor, nameKey, valueKeys, limit = 10) {
  const map = new Map();
  const perBucket = new Map(); // name -> { code: { key: value } }
  const bucketHasData = {}; // code -> true if THIS bucket contributed at least one row
  let found = false;
  codeList.forEach((code) => {
    bucketHasData[code] = false;
    const entry = buckets?.[code];
    if (!bucketSucceeded(entry)) return;
    const d = bucketData(entry);
    let rows = listAccessor(d);
    if (!Array.isArray(rows) && Array.isArray(d)) rows = d;
    if (!Array.isArray(rows) || rows.length === 0) return;
    found = true;
    bucketHasData[code] = true;
    rows.forEach((row) => {
      const name = row?.[nameKey];
      if (!name) return;
      const existing = map.get(name) || valueKeys.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
      const bucketVals = perBucket.get(name) || {};
      const bucketExisting = bucketVals[code] || valueKeys.reduce((acc, k) => ({ ...acc, [k]: 0 }), {});
      valueKeys.forEach((k) => {
        const v = Number(row?.[k]) || 0;
        existing[k] = (existing[k] || 0) + v;
        bucketExisting[k] = (bucketExisting[k] || 0) + v;
      });
      bucketVals[code] = bucketExisting;
      perBucket.set(name, bucketVals);
      map.set(name, existing);
    });
  });
  const primaryKey = valueKeys[0];
  const merged = Array.from(map.entries())
    .map(([name, values]) => {
      const bucketVals = perBucket.get(name) || {};
      const bucketCols = {};
      codeList.forEach((code) => {
        valueKeys.forEach((k) => {
          bucketCols[`bucket_${code}_${k}`] = bucketVals[code]?.[k] || 0;
        });
      });
      return { [nameKey]: name, ...bucketCols, ...values };
    })
    .sort((a, b) => b[primaryKey] - a[primaryKey])
    .slice(0, limit);
  return { rows: merged, found, bucketHasData };
}

// Month-name -> index, used to give "YYYY MonthName" strings (the shape
// flattenAnnualResult() produces, e.g. "2023 December") a real chronological
// sort key instead of sorting the raw string alphabetically. Alphabetical
// sort was the actual cause of the Annual Sales Trend x-axis showing
// "2023 December" before "2023 November", "2024 June" before "2024 May",
// etc. — D < N, J < M, and so on as plain strings.
const MONTH_INDEX = {
  January: 0, February: 1, March: 2, April: 3, May: 4, June: 5,
  July: 6, August: 7, September: 8, October: 9, November: 10, December: 11,
};
// Returns a numeric key that sorts real-world-chronologically for either
// shape of date string this file deals with:
//   - "YYYY MonthName"  (Annual Sales Trend, from flattenAnnualResult)
//   - "YYYY-MM-DD"        (Sales Volume / Colorant Consumption, raw from API)
// Falls back to the raw string (still usable by localeCompare) for anything
// unrecognized, so nothing ever throws — it just degrades to alphabetical.
function chronoSortKey(str) {
  const monthYear = /^(\d{4})\s+([A-Za-z]+)$/.exec(str);
  if (monthYear && MONTH_INDEX[monthYear[2]] !== undefined) {
    return Number(monthYear[1]) * 12 + MONTH_INDEX[monthYear[2]];
  }
  const parsed = Date.parse(str);
  if (!Number.isNaN(parsed)) return parsed;
  return str;
}
function chronoCompare(a, b) {
  const ka = chronoSortKey(a);
  const kb = chronoSortKey(b);
  if (typeof ka === 'number' && typeof kb === 'number') return ka - kb;
  return String(ka).localeCompare(String(kb));
}

// Same 5-year-back / no-future bounds applied to every custom date picker
// across the analytics section (single-bucket Dashboard.js and here) —
// matches the backend's own validate_date() limit, so a range that would
// pass the picker can never get rejected by the API.
const DATE_RANGE_MAX = () => new Date();
const DATE_RANGE_MIN = () => new Date(new Date().setFullYear(new Date().getFullYear() - 5));

// Safety net alongside the picker's own minDate/maxDate props: clamps
// start <= end, end <= today, start >= 5 years ago. The picker UI already
// enforces this visually, but a manually-typed or programmatically-set
// range should never be able to slip past it.
function clampDateRange(range) {
  const max = DATE_RANGE_MAX();
  const min = DATE_RANGE_MIN();
  let { startDate, endDate } = range || {};
  if (!startDate) startDate = min;
  if (!endDate) endDate = max;
  if (startDate > max) startDate = max;
  if (startDate < min) startDate = min;
  if (endDate > max) endDate = max;
  if (endDate < min) endDate = min;
  if (startDate > endDate) startDate = endDate; // start can never be after end
  return { startDate, endDate };
}

// merge date-indexed series across buckets (one line/bar per bucket) — used
// for true time-series charts like Sales Volume / Annual Sales Trend /
// Colorant Consumption
function mergeSeriesByBucket(buckets, codeList, rowsAccessor, dateKey, valueKey) {
  const series = [];
  const legend = [];
  const allDates = new Set();
  codeList.forEach((code, idx) => {
    const entry = buckets?.[code];
    let rows = [];
    if (bucketSucceeded(entry)) {
      const d = bucketData(entry);
      let r = rowsAccessor(d);
      if (!Array.isArray(r) && Array.isArray(d)) r = d;
      if (Array.isArray(r)) rows = r;
    }
    rows.forEach((r) => r?.[dateKey] && allDates.add(r[dateKey]));
    // FIX: previously this bucket was skipped entirely (both `legend.push`
    // and `series.push` below were behind `if (!bucketSucceeded...) return`
    // / `if (rows.length === 0) return`) whenever it had no data for this
    // specific card — e.g. bucket 7hnyo4 with no Annual Sales Trend rows
    // would silently vanish from that chart's legend, with no way to tell
    // it was even selected. Every selected bucket now always gets a legend
    // entry and a series entry — `hasData: false` buckets render as a flat
    // 0 line (the per-date fallback below, `row ? ... : 0`, already handles
    // this correctly) and still show up as a normal, clickable, colored
    // legend dot — echarts' built-in legend already toggles series
    // visibility on click, so this is "enable/disable" for free, not a new
    // control to build.
    legend.push(code);
    series.push({ code, rows, hasData: rows.length > 0, color: BUCKET_COLORS[idx % BUCKET_COLORS.length] });
  });
  const sortedDates = Array.from(allDates).sort(chronoCompare);
  return { series, legend, sortedDates };
}

// AnnualSalesTrendView's `result` is NOT a flat [{month,total}] list — it's
// grouped by year: [{year, yearly_sales, monthly_sales: [{month, total}]}].
// Flatten it here (prefixing with year, since `month` inside monthly_sales
// is just a bare month NAME like "July" with no year attached, so two
// different years' Julys would otherwise collide into one bar).
function flattenAnnualResult(result) {
  if (!Array.isArray(result)) return [];
  const flat = [];
  result.forEach((yearRow) => {
    (yearRow?.monthly_sales || []).forEach((m) => {
      flat.push({ month: `${yearRow.year} ${m.month}`, total: m.total });
    });
  });
  return flat;
}

// ColorantConsumptionViewSet's `result` rows look like
// {date, day, month, year, volume: {"<colorant name>": <litres>, ...}} —
// `volume` is a dict keyed by colorant, not a flat number. For the combined
// per-bucket line chart we sum every colorant's litres for that date into
// one total, same as the single-bucket page's overall consumption line.
function flattenColorantResult(result) {
  if (!Array.isArray(result)) return [];
  return result.map((row) => {
    const volumeObj = row?.volume || {};
    const litres = Object.values(volumeObj).reduce((sum, v) => sum + (Number(v) || 0), 0);
    return { date: row.date, litres };
  });
}

/* ---------------------------------------------------------------------- */

export default function CombinedAnalyticsDashboard({ codeList }) {
  const { t } = useTranslation('common');
  const classes = useStyles();
  const dispatch = useDispatch();

  const { ccMultiBucketLoading, ccMultiBucketData, ccMultiBucketError } = useSelector(
    (state) => state.analytics.analytics
  );

  const joined = codeList.join(',');

  const fetchAll = React.useCallback(() => {
    dispatch(getCCMultiBucketAnalytics('bucketInfo', 'cc_bucketAnalytics_multi_info', joined));
    dispatch(getCCMultiBucketAnalytics('machineStat', 'cc_bucketAnalytics_multi_MachineStat', joined));
    dispatch(getCCMultiBucketAnalytics('newShop', 'cc_bucketAnalytics_multi_NewShop', joined));
    dispatch(getCCMultiBucketAnalytics('location', 'cc_bucketAnalytics_multi_Location', joined));
    // Volume Tinted has its own date-range filter now (see fetchVolumeTinted
    // below), same as the single-bucket page's own Volume Tinted card.
    // no `top`/`worst` query param here on purpose — same as the single-bucket
    // page's getCCLocationBasedSales call, which sends only `code` and gets
    // BOTH top_ten and worst_ten back in one response; the toggle below is
    // purely client-side, just like on the single-bucket page.
    dispatch(getCCMultiBucketAnalytics('locationBasedSales', 'cc_bucketAnalytics_multi_LocationBasedSales', joined));
    dispatch(getCCMultiBucketAnalytics('customFormulaSales', 'cc_bucketAnalytics_multi_CustomFormulaSales', joined));
    // Sales Volume, Annual Sales Trend, Top Sales Statistics, Colorant
    // Consumption (+ its bar chart), and Top Can Sales each have their own
    // date/toggle controls now — same as the single-bucket page — so they
    // dispatch themselves below, independently, instead of being fetched here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, joined]);

  useEffect(() => {
    if (codeList.length === 0) return;
    fetchAll();
  }, [fetchAll, codeList.length]);

  /* ------------------------------ widgets ------------------------------ */
  const bucketInfo = useWidget(ccMultiBucketData, 'bucketInfo', codeList);
  const machineStat = useWidget(ccMultiBucketData, 'machineStat', codeList);
  const newShop = useWidget(ccMultiBucketData, 'newShop', codeList);
  const locationW = useWidget(ccMultiBucketData, 'location', codeList);
  const volumeTinted = useWidget(ccMultiBucketData, 'volumeTinted', codeList);
  const salesVolume = useWidget(ccMultiBucketData, 'salesVolume', codeList);
  const annualSalesTrend = useWidget(ccMultiBucketData, 'annualSalesTrend', codeList);
  const topTenColors = useWidget(ccMultiBucketData, 'topTenColors', codeList);
  const topProduct = useWidget(ccMultiBucketData, 'topProduct', codeList);
  const topSubProduct = useWidget(ccMultiBucketData, 'topSubProduct', codeList);
  const topBase = useWidget(ccMultiBucketData, 'topBase', codeList);
  const colorantConsumption = useWidget(ccMultiBucketData, 'colorantConsumption', codeList);
  const colorantConsumptionBar = useWidget(ccMultiBucketData, 'colorantConsumptionBar', codeList);
  const locationBasedSales = useWidget(ccMultiBucketData, 'locationBasedSales', codeList);
  const topCanSales = useWidget(ccMultiBucketData, 'topCanSales', codeList);
  const customFormulaSales = useWidget(ccMultiBucketData, 'customFormulaSales', codeList);

  // Which exportable report sections actually have data from at least one
  // selected bucket. Passed into MultiBucketAnalyticsExporter so it can
  // grey out / skip any section that would otherwise come back empty.
  const availableReports = {
    sales_volume: salesVolume.anySuccess,
    top_sub_product: topSubProduct.anySuccess,
    top_product: topProduct.anySuccess,
    top_ten_colors: topTenColors.anySuccess,
    colorant_consumption: colorantConsumption.anySuccess,
    top_base: topBase.anySuccess,
  };

  /* ---------------------------------------------------------------- */
  /* Per-section filters — same controls/params as the single-bucket   */
  /* page, each section fetching independently on its own state change */
  /* ---------------------------------------------------------------- */

  // ---- Sales Volume: Orders Tinted/Liters Sold + custom date range ----
  const [salesVolumeOrder, setSalesVolumeOrder] = React.useState(true); // true = order, false = litre
  const [salesVolumeStart, setSalesVolumeStart] = React.useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
  const [salesVolumeEnd, setSalesVolumeEnd] = React.useState(new Date());
  const [openSalesVolumeRange, setOpenSalesVolumeRange] = React.useState(false);
  const fetchSalesVolume = React.useCallback(() => {
    dispatch(getCCMultiBucketAnalytics('salesVolume', 'cc_bucketAnalytics_multi_SalesVolume', joined, {
      type: salesVolumeOrder ? 'order' : 'litre',
      start_date: fmtISO(salesVolumeStart),
      end_date: fmtISO(salesVolumeEnd),
    }));
  }, [dispatch, joined, salesVolumeOrder, salesVolumeStart, salesVolumeEnd]);
  useEffect(() => { if (codeList.length) fetchSalesVolume(); }, [fetchSalesVolume, codeList.length]);

  // ---- Annual Sales Trend: Orders Tinted/Liters Sold only ----
  const [annualSalesOrder, setAnnualSalesOrder] = React.useState(true);
  const fetchAnnualSalesTrend = React.useCallback(() => {
    dispatch(getCCMultiBucketAnalytics('annualSalesTrend', 'cc_bucketAnalytics_multi_AnnualSalesTrend', joined, {
      type: annualSalesOrder ? 'order' : 'litre',
    }));
  }, [dispatch, joined, annualSalesOrder]);
  useEffect(() => { if (codeList.length) fetchAnnualSalesTrend(); }, [fetchAnnualSalesTrend, codeList.length]);


  // Shared "last date type picked anywhere on this page" — every
  // preset-driven card below (Top Sales Statistics, Colorant Consumption
  // Bar, Top Can Sales, Volume Tinted) writes into this whenever the user
  // changes ITS OWN filter, and the Analytics Exporter / Scheduled Report
  // modals read it as their opening default. This is what makes "I picked
  // Last Year on one card" carry over as the default everywhere else,
  // without removing any card's ability to be set independently afterwards.
  // Same environment default the single-bucket dashboard opens with
  // (REACT_APP_DROPDOWN_DEFAULT_VALUE, e.g. "thismonth" on staging). This used
  // to be hard-coded to 'lastyear', so the combined page silently looked at a
  // different period than the single-bucket page and showed "No data found"
  // for buckets whose data is all in the current month.
  const [globalDateType, setGlobalDateType] = React.useState(
    process.env.REACT_APP_DROPDOWN_DEFAULT_VALUE || 'lastyear'
  );

  // ---- Top Sales Statistics (Colors/Product/Subproduct/Base): shared ----
  // Orders Tinted/Liters Sold toggle + one date-range dropdown, same as the
  // single-bucket page's "Top Sales Statistics" card header.
  const [topStatsOrder, setTopStatsOrder] = React.useState(true);
  const [topStatsDateType, setTopStatsDateType] = React.useState(globalDateType);
  const [topStatsDateLabel, setTopStatsDateLabel] = React.useState(() => labelForDateType(t, globalDateType));
  const topStatsSelectOption = (option) => () => {
    const dt = dateTypeFromLabel(t, option);
    setTopStatsDateType(dt);
    setTopStatsDateLabel(option);
    setGlobalDateType(dt);
  };
  const fetchTopStats = React.useCallback(() => {
    const params = { type: topStatsOrder ? 'order' : 'litre', date_type: topStatsDateType };
    dispatch(getCCMultiBucketAnalytics('topTenColors', 'cc_bucketAnalytics_multi_TopTenColors', joined, params));
    dispatch(getCCMultiBucketAnalytics('topProduct', 'cc_bucketAnalytics_multi_TopProduct', joined, params));
    dispatch(getCCMultiBucketAnalytics('topSubProduct', 'cc_bucketAnalytics_multi_TopSubProduct', joined, params));
    dispatch(getCCMultiBucketAnalytics('topBase', 'cc_bucketAnalytics_multi_TopBase', joined, params));
  }, [dispatch, joined, topStatsOrder, topStatsDateType]);
  useEffect(() => { if (codeList.length) fetchTopStats(); }, [fetchTopStats, codeList.length]);

  // ---- Colorant Consumption: custom date range (calendar button) ----
  const [colorantStart, setColorantStart] = React.useState(new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
  const [colorantEnd, setColorantEnd] = React.useState(new Date(new Date().setDate(new Date().getDate() - 1)));
  const [openColorantRange, setOpenColorantRange] = React.useState(false);
  const fetchColorantConsumption = React.useCallback(() => {
    dispatch(getCCMultiBucketAnalytics('colorantConsumption', 'cc_bucketAnalytics_multi_ColorantConsumption', joined, {
      start_date: fmtISO(colorantStart),
      end_date: fmtISO(colorantEnd),
    }));
  }, [dispatch, joined, colorantStart, colorantEnd]);
  useEffect(() => { if (codeList.length) fetchColorantConsumption(); }, [fetchColorantConsumption, codeList.length]);

  // ---- Colorant Consumption Bar: date-range dropdown ----
  const [colorantBarDateType, setColorantBarDateType] = React.useState(globalDateType);
  const [colorantBarDateLabel, setColorantBarDateLabel] = React.useState(() => labelForDateType(t, globalDateType));
  const colorantBarSelectOption = (option) => () => {
    const dt = dateTypeFromLabel(t, option);
    setColorantBarDateType(dt);
    setColorantBarDateLabel(option);
    setGlobalDateType(dt);
  };
  const fetchColorantConsumptionBar = React.useCallback(() => {
    dispatch(getCCMultiBucketAnalytics('colorantConsumptionBar', 'cc_bucketAnalytics_multi_ColorantConsumptionBar', joined, {
      date_type: colorantBarDateType,
    }));
  }, [dispatch, joined, colorantBarDateType]);
  useEffect(() => { if (codeList.length) fetchColorantConsumptionBar(); }, [fetchColorantConsumptionBar, codeList.length]);

  // ---- Top Can Sales: date-range dropdown ----
  const [topCanSalesDateType, setTopCanSalesDateType] = React.useState(globalDateType);
  const [topCanSalesDateLabel, setTopCanSalesDateLabel] = React.useState(() => labelForDateType(t, globalDateType));
  const topCanSalesSelectOption = (option) => () => {
    const dt = dateTypeFromLabel(t, option);
    setTopCanSalesDateType(dt);
    setTopCanSalesDateLabel(option);
    setGlobalDateType(dt);
  };
  const fetchTopCanSales = React.useCallback(() => {
    dispatch(getCCMultiBucketAnalytics('topCanSales', 'cc_bucketAnalytics_multi_TopCanSales', joined, {
      date_type: topCanSalesDateType,
    }));
  }, [dispatch, joined, topCanSalesDateType]);
  useEffect(() => { if (codeList.length) fetchTopCanSales(); }, [fetchTopCanSales, codeList.length]);

  // ---- Volume Tinted: its own date-range filter (This Year/This Month/etc.) ----
  // Same filterButton/filter/handleChange pattern the single-bucket page's own
  // Volume Tinted card uses, just re-summed across every selected bucket.
  const [volumeTintedFilter, setVolumeTintedFilter] = React.useState(globalDateType);
  const fetchVolumeTinted = React.useCallback((dt) => {
    dispatch(getCCMultiBucketAnalytics('volumeTinted', 'cc_bucketAnalytics_multi_VolumeTinted', joined, { date_type: dt || volumeTintedFilter }));
  }, [dispatch, joined, volumeTintedFilter]);
  useEffect(() => { if (codeList.length) fetchVolumeTinted(volumeTintedFilter); }, [fetchVolumeTinted, codeList.length]);
  const handleVolumeTintedFilterChange = (event) => {
    setVolumeTintedFilter(event.target.value);
    setGlobalDateType(event.target.value);
    fetchVolumeTinted(event.target.value);
  };

  /* ---- combined KPI numbers (top row cards) ---- */
  const dataUsageTotal = sumField(bucketInfo.buckets, codeList, (d) => d?.data_usage);
  const recordsTotal = sumField(bucketInfo.buckets, codeList, (d) => d?.records);
  const volumeTintedTotal = sumField(volumeTinted.buckets, codeList, (d) => d?.volume_tinted);
  // BUG FIX: this used to be sumField(..., (d) => d?.avg_volume) — literally
  // adding each bucket's own average together. That's not a combined average
  // (summing 5 per-bucket averages of ~1L each gave ~5L, not ~1L), and
  // summing several 4-decimal floats in JS also produces long
  // floating-point tails (e.g. 5.325200000000001) that were rendered
  // unrounded and blew out the card's width. The correct combined average is
  // total volume / total orders across the selected buckets, rounded to the
  // same 4 decimals the backend uses (see round(...,4) in
  // util/queries.py:get_VolumeTinted_query).
  const ordersTotal = sumField(volumeTinted.buckets, codeList, (d) => d?.orders);
  const avgVolumeTotal = {
    found: volumeTintedTotal.found,
    total: ordersTotal.total > 0
      ? Number((volumeTintedTotal.total / ordersTotal.total).toFixed(4))
      : 0,
  };
  const newShopsTotal = sumField(newShop.buckets, codeList, (d) => d?.new_shops);
  const locationsTotal = sumField(locationW.buckets, codeList, (d) => d?.no_of_shops);

  /* ---- combined machine-deployment doughnut (+ POS-type / version ring) ---- */
  const machineStatChart = useMemo(() => {
    let online = 0, offline = 0, nonOperational = 0, found = false;
    let newerVersions = 0, olderVersions = 0;
    const posTypeMap = new Map(); // pos_type -> count, combined across buckets
    codeList.forEach((code) => {
      const entry = machineStat.buckets?.[code];
      if (!bucketSucceeded(entry)) return;
      const stats = entry?.data?.operation_statistics;
      if (stats) {
        found = true;
        online += Number(stats.online || 0);
        offline += Number(stats.offline || 0);
        nonOperational += Number(stats.non_operational || 0);
      }
      const versionStats = entry?.data?.version_statistics;
      if (versionStats) {
        newerVersions += Number(versionStats.newer_versions || 0);
        olderVersions += Number(versionStats.older_versions || 0);
      }
      const typeStats = entry?.data?.type_statistics;
      if (Array.isArray(typeStats)) {
        typeStats.forEach((row) => {
          if (!row?.pos_type) return;
          posTypeMap.set(row.pos_type, (posTypeMap.get(row.pos_type) || 0) + Number(row.count || 0));
        });
      }
    });
    const posTypeData = Array.from(posTypeMap.entries()).map(([name, value], idx) => ({
      value,
      name,
      itemStyle: { color: POS_TYPE_COLORS[idx % POS_TYPE_COLORS.length] },
    }));
    return {
      found,
      total: online + offline + nonOperational,
      online,
      offline,
      nonOperational,
      newerVersions,
      olderVersions,
      posTypeData,
      hasPosTypeRing: posTypeData.length > 0 || newerVersions > 0 || olderVersions > 0,
    };
  }, [machineStat.buckets, codeList]);

  /* ---- Sales Volume (per-bucket line series, true time series) ---- */
  // BUG FIX: SalesVolumeView returns a different value column depending on
  // `type` — {date, order} for type=order, {date, volume} for type=litre
  // (see cc_api/bucket_analytics/views.py SalesVolumeView.list/build_data_for_code).
  // This was previously hardcoded to 'volume' below, so the Orders Tinted
  // view (salesVolumeOrder === true, the default) always read a field that
  // doesn't exist on order-type rows and silently fell back to 0 for every
  // date — flat zero line on the chart, and all-zero rows in the export.
  const salesVolumeValueKey = salesVolumeOrder ? 'order' : 'volume';
  const salesVolumeMerged = useMemo(
    () => mergeSeriesByBucket(salesVolume.buckets, codeList, (d) => d?.result, 'date', salesVolumeValueKey),
    [salesVolume.buckets, codeList, salesVolumeValueKey]
  );
  const salesVolumeChartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: salesVolumeMerged.legend, top: 0 },
    // same percentage-based grid the single-bucket page's own Sales Volume
    // chart uses (Dashboard.js ~line 2827-2831) — containLabel:true + a %
    // bottom margin is what reserves exactly the space the dataZoom slider
    // needs, instead of a fixed pixel value that overlaps sparse data
    grid: { left: '3%', right: '4%', bottom: '15%', top: 40, containLabel: true },
    xAxis: { type: 'category', data: salesVolumeMerged.sortedDates, boundaryGap: false },
    yAxis: { type: 'value' },
    dataZoom: DATA_ZOOM_CONFIG,
    color: salesVolumeMerged.series.map((s) => s.color),
    series: salesVolumeMerged.series.map((s) => ({
      name: s.code,
      type: 'line',
      smooth: true,
      data: salesVolumeMerged.sortedDates.map((d) => {
        const row = s.rows.find((r) => r.date === d);
        return row ? Number(row[salesVolumeValueKey] || 0) : 0;
      }),
    })),
  };

  /* ---- Annual Sales Trend (per-bucket bar series) ---- */
  const annualMerged = useMemo(
    () => mergeSeriesByBucket(annualSalesTrend.buckets, codeList, (d) => flattenAnnualResult(d?.result), 'month', 'total'),
    [annualSalesTrend.buckets, codeList]
  );
  // View Graph / View Table toggle, same as the single-bucket page's own
  // Annual Sales Trend card. The single-bucket table is one bucket x 12
  // month rows x year columns; with multiple buckets combined we need one
  // column per (bucket, year) pair instead of just per year, so this is
  // derived generically from the already-merged series rather than copying
  // the single-bucket page's hardcoded 12-month markup.
  const [annualViewMode, setAnnualViewMode] = React.useState('graph');
  const annualTrendTable = useMemo(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const columns = [];
    annualMerged.series.forEach((s) => {
      const years = new Set();
      s.rows.forEach((r) => { years.add(r.month.split(' ')[0]); });
      Array.from(years).sort().forEach((year) => columns.push({ code: s.code, year, rows: s.rows }));
    });
    return { months, columns };
  }, [annualMerged]);
  const annualChartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: annualMerged.legend, top: 0 },
    // matches Dashboard.js's own Annual Sales Trend grid exactly (line
    // ~3070-3075): 3% bottom + containLabel, and — critically — NO
    // dataZoom. The single-bucket page never puts a zoom slider on this
    // chart; the combined version previously added one plus a fixed 60px
    // bottom margin, which is what overlapped the bars when only a couple
    // of months had data.
    grid: { left: '3%', right: '4%', bottom: '3%', top: 40, containLabel: true },
    xAxis: { type: 'category', boundaryGap: false, data: annualMerged.sortedDates, axisLabel: { showMaxLabel: true } },
    yAxis: { type: 'value', axisLabel: { formatter: '{value}', margin: 10 } },
    textStyle: { fontFamily: 'Roboto' },
    color: annualMerged.series.map((s) => s.color),
    series: annualMerged.series.map((s) => ({
      name: s.code,
      type: 'line',
      smooth: true,
      data: annualMerged.sortedDates.map((d) => {
        const row = s.rows.find((r) => r.month === d);
        return row ? Number(row.total || 0) : 0;
      }),
    })),
  };

  /* ---- Top Sales Statistics: Colors / Product / Subproduct / Base ---- */
  const [statTab, setStatTab] = React.useState('product');
  const colorsMerged = mergeTopList(topTenColors.buckets, codeList, (d) => d?.result, 'color', 'sales_volume');
  // The backend names the value column by report type: `orders` for Orders
  // Tinted, `volume` for Liters Sold. Hard-coding 'orders' summed every row to
  // 0 whenever Liters Sold was selected.
  const topStatsValueKey = topStatsOrder ? 'orders' : 'volume';
  const productsMerged = mergeTopList(topProduct.buckets, codeList, (d) => d?.result, 'product', topStatsValueKey);
  const subProductsMerged = mergeTopList(topSubProduct.buckets, codeList, (d) => d?.result, 'subproduct', topStatsValueKey);
  const baseMerged = mergeTopList(topBase.buckets, codeList, (d) => d?.result, 'base', topStatsValueKey);
  const statTabData = { product: productsMerged, subproduct: subProductsMerged, base: baseMerged }[statTab];
  const statTabWidgetKey = { product: 'topProduct', subproduct: 'topSubProduct', base: 'topBase' }[statTab];
  const statTabWidget = { topProduct, topSubProduct, topBase }[statTabWidgetKey];

  /* ---- Colorant Consumption (line) + bar ---- */
  const colorantMerged = useMemo(
    () => mergeSeriesByBucket(colorantConsumption.buckets, codeList, (d) => flattenColorantResult(d?.result), 'date', 'litres'),
    [colorantConsumption.buckets, codeList]
  );
  // Colorant Consumption Bar has no worst_ten field from the backend (unlike
  // Location Based Sales) — this "Worst Ten" is computed client-side from the
  // same combined dataset by sorting ascending instead of descending.
  const [colorantBarTopTen, setColorantBarTopTen] = React.useState(true);
  const colorantConsumptionBarMerged = mergeTopList(
    colorantConsumptionBar.buckets, codeList, (d) => d?.result, 'colorant', 'colorant_qnt_agg',
    10, colorantBarTopTen ? 'top' : 'worst'
  );

  /* ---- Location Based Sales (top 10 / worst 10 combined) ---- */
  // Same two fields the single-bucket page charts (first_quarter_sales_cnt +
  // second_quarter_sales_cnt), summed per POS across every bucket, for
  // whichever list (top_ten / worst_ten) the toggle currently shows.
  const [locationBasedTopTen, setLocationBasedTopTen] = React.useState(true);
  const locationBasedTopMerged = mergeTopListMultiSum(
    locationBasedSales.buckets, codeList, (d) => d?.result?.top_ten, 'pos',
    ['first_quarter_sales_cnt', 'second_quarter_sales_cnt']
  );
  const locationBasedWorstMerged = mergeTopListMultiSum(
    locationBasedSales.buckets, codeList, (d) => d?.result?.worst_ten, 'pos',
    ['first_quarter_sales_cnt', 'second_quarter_sales_cnt']
  );
  const locationBasedMerged = locationBasedTopTen ? locationBasedTopMerged : locationBasedWorstMerged;

  /* ---- Top Can Sales ---- */
  const topCanSalesMerged = mergeTopList(topCanSales.buckets, codeList, (d) => d?.result, 'can_size', 'sale_volume');

  /* ---- Custom Formula vs Regular (combined pie) ---- */
  const customFormulaCombined = useMemo(() => {
    let regular = 0, custom = 0, found = false;
    const perBucketRows = [];
    const bucketHasData = {}; // code -> true if THIS bucket contributed data
    codeList.forEach((code) => {
      bucketHasData[code] = false;
      const entry = customFormulaSales.buckets?.[code];
      if (!bucketSucceeded(entry)) return;
      const d = bucketData(entry);
      if (!d) return;
      found = true;
      bucketHasData[code] = true;
      const bucketRegular = Number(d.regular || 0);
      const bucketCustom = Number(d.custom_formula || 0);
      regular += bucketRegular;
      custom += bucketCustom;
      perBucketRows.push({ bucket_id: code, regular: bucketRegular, custom_formula: bucketCustom, total: bucketRegular + bucketCustom });
    });
    return { found, regular, custom, total: regular + custom, perBucketRows, bucketHasData };
  }, [customFormulaSales.buckets, codeList]);

  const anyLoading = codeList.length > 0 && Object.values(ccMultiBucketLoading || {}).some(Boolean);
  // how many of the selected buckets actually contributed to AT LEAST one
  // widget so far — shown on the KPI card so it's obvious this is a partial
  // combination when some buckets have nothing
  const contributingBucketCount = useMemo(() => {
    let count = 0;
    codeList.forEach((code) => {
      const anyHit = [bucketInfo, machineStat, newShop, locationW, volumeTinted, salesVolume,
        annualSalesTrend, topTenColors, topProduct, topSubProduct, topBase,
        colorantConsumption, colorantConsumptionBar, locationBasedSales, topCanSales, customFormulaSales]
        .some((w) => bucketSucceeded(w.buckets?.[code]));
      if (anyHit) count += 1;
    });
    return count;
  }, [codeList, bucketInfo, machineStat, newShop, locationW, volumeTinted, salesVolume,
    annualSalesTrend, topTenColors, topProduct, topSubProduct, topBase,
    colorantConsumption, colorantConsumptionBar, locationBasedSales, topCanSales, customFormulaSales]);

  return (
    <div>
      <MultiBucketAnalyticsExporter codeList={codeList} availableReports={availableReports} defaultDateType={globalDateType} />

      {anyLoading && (
        <div style={{ textAlign: 'center', padding: 10 }}>
          <CircularProgress size={22} sx={{ color: '#C8102E' }} />
        </div>
      )}

      {/* ================= TOP KPI CARDS — identical styling to single-bucket page ================= */}
      <FirstSection>
      <RootContainer>
        <div className={classes.cardDiv}>
          <DashboardCard
            errorStatus={!bucketInfo.anySuccess && ccMultiBucketError?.bucketInfo}
            tryClick={fetchAll}
            errorTopheight={'translateY(80%)'}
            title={t('XdataAnalyticsDetail.BucketInformation')}
            height={200}
            icon="document"
            columnInfo3={true}
            columnInfo3Data={{
              column1Title: t('MultiBucketAnalytics.bucketsCombined') || 'Buckets combined',
              column1Desc: `${contributingBucketCount} / ${codeList.length}`,
              column2Title: t('XdataAnalyticsDetail.DataUsage'),
              column2Desc: dataUsageTotal.found ? dataUsageTotal.total.toFixed(2) + ' MB' : null,
              column3Title: t('MultiBucketAnalytics.records'),
              column3Desc: recordsTotal.found ? recordsTotal.total : null,
            }}
            loading={ccMultiBucketLoading?.bucketInfo}
          />
          <DashboardCard
            errorStatus={!volumeTinted.anySuccess && ccMultiBucketError?.volumeTinted}
            tryClick={() => fetchVolumeTinted()}
            errorTopheight={'translateY(80%)'}
            title={t('XdataAnalyticsDetail.VolumeOrdersTinted')}
            height={200}
            filterButton={true}
            filter={volumeTintedFilter}
            handleChange={handleVolumeTintedFilterChange}
            columnInfo2={true}
            columnInfo2Data={{
              column1Title: t('XdataAnalyticsDetail.Volume'),
              // .toFixed(4) here too: same float-accumulation issue as
              // avgVolumeTotal below, since this is also a running sum of
              // several already-rounded per-bucket floats.
              column1Desc: volumeTintedTotal.found ? Number(volumeTintedTotal.total.toFixed(4)) + 'L' : null,
              column2Title: t('XdataAnalyticsDetail.avgVolume'),
              column2Desc: avgVolumeTotal.found ? avgVolumeTotal.total.toFixed(4) + 'L' : null,
            }}
            loading={ccMultiBucketLoading?.volumeTinted}
          />
        </div>
        <div className={classes.cardDiv}>
          <DashboardCard
            errorStatus={!newShop.anySuccess && ccMultiBucketError?.newShop}
            tryClick={fetchAll}
            errorTopheight={'translateY(80%)'}
            title={t('XdataAnalyticsDetail.NewShopsOpened')}
            height={200}
            icon="shop"
            footerLeftContent={{
              title: newShopsTotal.found ? newShopsTotal.total : 0,
              desc: t('XdataAnalyticsDetail.NewShopsOpenedThisYear'),
            }}
            loading={ccMultiBucketLoading?.newShop}
          />
          <DashboardCard
            errorStatus={!locationW.anySuccess && ccMultiBucketError?.location}
            tryClick={fetchAll}
            errorTopheight={'translateY(80%)'}
            title={t('XdataAnalyticsDetail.Locations')}
            height={200}
            icon="location"
            footerLeftContent={{
              title: locationsTotal.found ? locationsTotal.total : 0,
            }}
            loading={ccMultiBucketLoading?.location}
          />
        </div>
      </RootContainer>

      <MachineStatistics>
        <DashboardCard
          total={machineStatChart.total}
          errorStatus={!machineStat.anySuccess && ccMultiBucketError?.machineStat}
          tryClick={fetchAll}
          errorTopheight={'translateY(80%)'}
          title={t('XdataAnalyticsDetail.MachineDeploymentStatistics')}
          width="100%"
          height={430}
          loading={ccMultiBucketLoading?.machineStat}
          chart={
            machineStatChart.found || machineStatChart.hasPosTypeRing
              ? {
                  DoughnutChart: machineStatChart.found ? {
                    tooltip: { trigger: 'item' },
                    legend: { bottom: 0, align: 'left' },
                    title: { text: t('XdataAnalyticsDetail.Total') + ':' + machineStatChart.total, left: 'center', top: 'center' },
                    textStyle: { fontFamily: 'Roboto' },
                    series: [{
                      bottom: 15,
                      name: '',
                      type: 'pie',
                      radius: ['45%', '70%'],
                      avoidLabelOverlap: false,
                      label: { show: false, position: 'center' },
                      data: [
                        { value: machineStatChart.nonOperational, name: t('XdataAnalyticsDetail.NonOperational'), itemStyle: { color: '#C8102E' } },
                        { value: machineStatChart.online, name: t('XdataAnalyticsDetail.Online'), itemStyle: { color: '#36333d' } },
                        { value: machineStatChart.offline, name: t('XdataAnalyticsDetail.Offline'), itemStyle: { color: '#a2aaad' } },
                      ],
                    }],
                  } : undefined,
                  // second ring — POS type breakdown (outer) + version breakdown
                  // (inner), combined across every bucket that returned data,
                  // exactly like the single-bucket page's NestedPie chart
                  NestedPie: machineStatChart.hasPosTypeRing ? {
                    tooltip: { trigger: 'item' },
                    legend: { bottom: -7, align: 'left' },
                    textStyle: { fontFamily: 'Roboto' },
                    series: [
                      {
                        stillShowZeroSum: false,
                        name: '',
                        type: 'pie',
                        radius: ['45%', '70%'],
                        labelLine: { length: 30 },
                        label: { show: false },
                        data: machineStatChart.posTypeData,
                        bottom: 15,
                      },
                      {
                        bottom: 15,
                        stillShowZeroSum: false,
                        name: '',
                        type: 'pie',
                        selectedMode: 'single',
                        radius: [0, '25%'],
                        label: { show: false },
                        labelLine: { show: false },
                        selectedOffset: 0,
                        data: [
                          { value: machineStatChart.newerVersions, name: t('XdataAnalyticsDetail.NewerVersions'), itemStyle: { color: '#FF2C4F' } },
                          { value: machineStatChart.olderVersions, name: t('XdataAnalyticsDetail.OlderVersions'), itemStyle: { color: '#5A5762' }, selected: true },
                        ],
                      },
                    ],
                  } : undefined,
                }
              : undefined
          }
        />
      </MachineStatistics>
      </FirstSection>

      {/* ================= SALES VOLUME ================= */}
      <WhiteCard>
        <div className={classes.datechartHeader}>
          <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.SalesVolume')}</h2>
          <div className={classes.chartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            <div className="radioButtonStyle" style={{ display: 'inline-grid' }}>
              <FormControlLabel name="radio-buttons" value={true}
                control={<Radio checked={salesVolumeOrder === true} onChange={() => setSalesVolumeOrder(true)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.OrdersTinted')} />
              <FormControlLabel style={{ marginTop: 3 }} name="radio-buttons" value={false}
                control={<Radio checked={salesVolumeOrder === false} onChange={() => setSalesVolumeOrder(false)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.LitersSold')} />
            </div>
            <ChartButton onClick={() => setOpenSalesVolumeRange(!openSalesVolumeRange)} style={{ height: 32, paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
              <CalendarIcon className={classes.CalendarIcon} /> {fmtDisplay(salesVolumeStart)} - {fmtDisplay(salesVolumeEnd)}
            </ChartButton>
            <Tooltip title={t('xdataLocationDetails.reload')}>
              <Refresh onClick={fetchSalesVolume} style={{ padding: 7, marginLeft: 5 }}>
                <AiOutlineReload />
              </Refresh>
            </Tooltip>
            <DownloadButton
              t={t}
              disabled={salesVolumeMerged.series.every((s) => !s.hasData)}
              filename="Sales Volume (Combined)"
              data={salesVolumeMerged.sortedDates.map((d) => {
                const row = { date: d };
                salesVolumeMerged.series.forEach((s) => {
                  row[s.code] = s.rows.find((r) => r.date === d)?.[salesVolumeValueKey] ?? 0;
                });
                return row;
              })}
            />
          </div>
        </div>
        <DateRangePicker
          open={openSalesVolumeRange}
          toggle={() => setOpenSalesVolumeRange(!openSalesVolumeRange)}
          maxDate={DATE_RANGE_MAX()}
          minDate={DATE_RANGE_MIN()}
          onChange={(range) => {
            const clamped = clampDateRange(range);
            setSalesVolumeStart(clamped.startDate);
            setSalesVolumeEnd(clamped.endDate);
            setOpenSalesVolumeRange(false);
          }}
          wrapperClassName={classes.wrapperClassNameSalesVolume}
        />
        {ccMultiBucketLoading?.salesVolume ? (
          <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
        ) : !salesVolume.anySuccess && ccMultiBucketError?.salesVolume ? (
          <ErrorBlock btnClick={fetchSalesVolume} />
        ) : salesVolumeMerged.series.every((s) => !s.hasData) ? (
          <SectionPlaceholder>
            <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
            <BucketStatusStrip codeList={codeList} />
          </SectionPlaceholder>
        ) : (
          <>
            <AreaChart chartInfo={salesVolumeChartOption} />
            {salesVolumeOrder === false && (
              <div style={{ display: 'flex', marginLeft: 40, marginTop: 10 }}>
                <BiInfoCircle style={{ fontSize: 14, marginRight: 5 }} />
                <p style={{ fontFamily: 'Roboto', fontSize: 11 }}>{t('XdataAnalyticsDetail.allValuesinLitre')}</p>
              </div>
            )}
          </>
        )}
      </WhiteCard>

      {/* ================= ANNUAL SALES TREND ================= */}
      <WhiteCard>
        <div className={classes.datechartHeader}>
          <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.AnnualSalesTrend')}</h2>
          <div className={classes.chartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            <div className="radioButtonStyle" style={{ display: 'inline-grid' }}>
              <FormControlLabel name="radio-buttons" value={true}
                control={<Radio checked={annualSalesOrder === true} onChange={() => setAnnualSalesOrder(true)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.OrdersTinted')} />
              <FormControlLabel style={{ marginTop: 3 }} name="radio-buttons" value={false}
                control={<Radio checked={annualSalesOrder === false} onChange={() => setAnnualSalesOrder(false)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.LitersSold')} />
            </div>
            <Tooltip title={t('xdataLocationDetails.reload')}>
              <Refresh onClick={fetchAnnualSalesTrend} style={{ padding: 7, marginLeft: 5 }}>
                <AiOutlineReload />
              </Refresh>
            </Tooltip>
            <ButtonDropdown
              label={t('XdataAnalyticsDetail.ViewGraph')}
              options={[t('XdataAnalyticsDetail.ViewGraph'), t('XdataAnalyticsDetail.ViewTable')]}
              selectedOption={annualViewMode === 'table' ? t('XdataAnalyticsDetail.ViewTable') : t('XdataAnalyticsDetail.ViewGraph')}
              annualSalesTrendSelectOption={(option) => () => setAnnualViewMode(option === t('XdataAnalyticsDetail.ViewTable') ? 'table' : 'graph')}
            />
            <DownloadButton
              t={t}
              disabled={annualMerged.series.every((s) => !s.hasData)}
              filename="Annual Sales Trend (Combined)"
              data={annualMerged.sortedDates.map((d) => {
                const row = { month: d };
                annualMerged.series.forEach((s) => {
                  row[s.code] = s.rows.find((r) => r.month === d)?.total ?? 0;
                });
                return row;
              })}
            />
          </div>
        </div>
        {ccMultiBucketLoading?.annualSalesTrend ? (
          <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
        ) : !annualSalesTrend.anySuccess && ccMultiBucketError?.annualSalesTrend ? (
          <ErrorBlock btnClick={fetchAnnualSalesTrend} />
        ) : annualMerged.series.every((s) => !s.hasData) ? (
          <SectionPlaceholder>
            <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
            <BucketStatusStrip codeList={codeList} />
          </SectionPlaceholder>
        ) : annualViewMode === 'table' ? (
          <Table sx={{ marginTop: 3 }} size="small">
            <TableHead>
              <TableRow sx={{ 'td, th': { border: '1px solid #A7A7A7', background: '#DDDDDD', fontWeight: 'bold' } }}>
                <TableCell></TableCell>
                {annualTrendTable.columns.map((c) => (
                  <TableCell key={`${c.code}-${c.year}`} align="center">{c.code} — {c.year}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {annualTrendTable.months.map((month) => (
                <TableRow key={month} sx={{ 'td': { border: '1px solid #A7A7A7', color: '#36333D' }, 'th': { border: '1px solid #A7A7A7', color: '#36333D', fontWeight: 'bold' } }}>
                  <TableCell align="center" component="th">{t(`XdataAnalyticsDetail.${month.slice(0, 3)}`)}</TableCell>
                  {annualTrendTable.columns.map((c) => {
                    const row = c.rows.find((r) => r.month === `${c.year} ${month}`);
                    return <TableCell key={`${c.code}-${c.year}-${month}`} align="center">{row ? row.total : ''}</TableCell>;
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <>
            <AreaChart chartInfo={annualChartOption} />
            {annualSalesOrder === false && (
              <div style={{ display: 'flex', marginLeft: 20, marginTop: 10 }}>
                <BiInfoCircle style={{ fontSize: 14, marginRight: 5 }} />
                <p style={{ fontFamily: 'Roboto', fontSize: 11 }}>{t('XdataAnalyticsDetail.allValuesinLitre')}</p>
              </div>
            )}
          </>
        )}
      </WhiteCard>

      {/* ================= TOP SALES STATISTICS ================= */}
      <WhiteCard>
        <div className={classes.datechartHeader}>
          <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.TopSalesStatistics')}</h2>
          <div className={classes.chartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            <div className="radioButtonStyle" style={{ display: 'inline-grid' }}>
              <FormControlLabel name="radio-buttons" value={true}
                control={<Radio checked={topStatsOrder === true} onChange={() => setTopStatsOrder(true)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.OrdersTinted')} />
              <FormControlLabel style={{ marginTop: 3 }} name="radio-buttons" value={false}
                control={<Radio checked={topStatsOrder === false} onChange={() => setTopStatsOrder(false)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.LitersSold')} />
            </div>
            <ButtonDropdown
              label={t('XdataAnalyticsDetail.lastyear')}
              options={[
                t('XdataAnalyticsDetail.lastyear'), t('XdataAnalyticsDetail.lastmonth'),
                t('XdataAnalyticsDetail.thisyear'), t('XdataAnalyticsDetail.thismonth'),
                t('XdataAnalyticsDetail.currentQuarter'), t('XdataAnalyticsDetail.prevQuarter'),
              ]}
              selectedOption={topStatsDateLabel}
              annualSalesTrendSelectOption={topStatsSelectOption}
            />
            <Tooltip title={t('xdataLocationDetails.reload')}>
              <Refresh onClick={fetchTopStats} style={{ padding: 7, marginLeft: 5 }}>
                <AiOutlineReload />
              </Refresh>
            </Tooltip>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div className={classes.datechartHeader}>
            <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.Colors')}</h2>
            <DownloadButton t={t} disabled={colorsMerged.rows.length === 0} filename="Top Colors (Combined)" data={colorsMerged.rows} />
          </div>
          {ccMultiBucketLoading?.topTenColors ? (
            <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
          ) : !topTenColors.anySuccess && ccMultiBucketError?.topTenColors ? (
            <ErrorBlock btnClick={fetchTopStats} />
          ) : (
            <>
              <BucketChipStrip codeList={codeList} hasDataByCode={colorsMerged.bucketHasData} />
              {colorsMerged.rows.length === 0 ? (
                <SectionPlaceholder>
                  <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
                </SectionPlaceholder>
              ) : (
            <FunnelChart
              chartInfo={{
                tooltip: {
                  trigger: 'item',
                  formatter: function (params) {
                    return `${params.seriesName} <br/><b>${params.name}</b>`;
                  },
                },
                legend: {
                  right: 50,
                  orient: 'vertical',
                },
                textStyle: { fontFamily: 'Roboto' },
                series: [
                  {
                    left: '0%',
                    name: t('XdataAnalyticsDetail.Colors'),
                    type: 'funnel',
                    sort: 'ascending', // same as the single-bucket page's Top Ten Colors funnel
                    width: '50%',
                    gap: 4,
                    // same short label as the single-bucket funnel: only the
                    // part before '||', max 10 chars, plus the value. Full
                    // names ran across the legend on the right.
                    label: {
                      show: true,
                      color: 'black',
                      formatter: (params) => {
                        const value = params.name;
                        const idx = value.lastIndexOf(' : ');
                        const namePart = idx === -1 ? value : value.slice(0, idx);
                        const valuePart = idx === -1 ? '' : value.slice(idx + 3);
                        return `${namePart.split('||')[0].trim().substring(0, 10)} : ${valuePart.trim()}`;
                      },
                    },
                    labelLine: {
                      length: 30,
                      lineStyle: { width: 1, type: 'solid' },
                    },
                    itemStyle: { borderColor: '#fff', borderWidth: 1 },
                    emphasis: { label: { fontSize: 20 } },
                    // colorsMerged.rows is already the TRUE combined total —
                    // sales_volume summed by color name across every
                    // selected bucket, re-ranked, top 10 — see mergeTopList()
                    // above. Only the chart shape changes here, not the data.
                    // Layer width is rank-based ((idx + 1) * 10), exactly like
                    // the single-bucket funnel, so it's always a clean
                    // pyramid with the top seller at the tip. Using the raw
                    // sales_volume made equal values render as same-width
                    // blocks. The real number is still shown in the label.
                    data: colorsMerged.rows.map((r, idx) => ({
                      name: `${r.color} : ${r.sales_volume}`,
                      value: (idx + 1) * 10,
                      itemStyle: { color: r.hex_color || '#C8102E' },
                    })),
                  },
                ],
              }}
            />
              )}
            </>
          )}
        </div>

        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
            {['product', 'subproduct', 'base'].map((tab) => (
              <Chip
                key={tab}
                label={t(`XdataAnalyticsDetail.${tab === 'product' ? 'Product' : tab === 'subproduct' ? 'Subproduct' : 'Base'}`)}
                onClick={() => setStatTab(tab)}
                style={{
                  background: statTab === tab ? '#C8102E' : '#fff',
                  color: statTab === tab ? '#fff' : '#36333D',
                  border: '1px solid #C8102E',
                }}
              />
            ))}
            <DownloadButton
              t={t}
              disabled={statTabData.rows.length === 0}
              filename={`Top ${statTab} (Combined)`}
              data={statTabData.rows}
            />
          </div>
          {ccMultiBucketLoading?.[statTab === 'product' ? 'topProduct' : statTab === 'subproduct' ? 'topSubProduct' : 'topBase'] ? (
            <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
          ) : !statTabWidget.anySuccess && ccMultiBucketError?.[statTabWidgetKey] ? (
            <ErrorBlock btnClick={fetchTopStats} />
          ) : (
            <>
              <BucketChipStrip codeList={codeList} hasDataByCode={statTabData.bucketHasData} />
              {statTabData.rows.length === 0 ? (
                <SectionPlaceholder>
                  <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
                </SectionPlaceholder>
              ) : (
                <BarChart
                  chartInfo={{
                    tooltip: { trigger: 'axis' },
                    grid: { left: '3%', right: '4%', bottom: '6%', containLabel: true },
                    xAxis: { type: 'category', data: statTabData.rows.map((r) => r[statTab]) },
                    yAxis: { type: 'value' },
                    // combined total per row, NOT Object.values(r)[1] — after
                    // mergeTopList that is the first bucket's own column.
                    series: [{ data: statTabData.rows.map((r) => Number(r[topStatsValueKey]) || 0), type: 'bar', itemStyle: { color: '#36333d' } }],
                  }}
                />
              )}
            </>
          )}
          {topStatsOrder === false && (
            <div style={{ display: 'flex', marginTop: 10 }}>
              <BiInfoCircle style={{ fontSize: 14, marginRight: 5 }} />
              <p style={{ fontFamily: 'Roboto', fontSize: 11 }}>{t('XdataAnalyticsDetail.allValuesinLitre')}</p>
            </div>
          )}
        </div>
      </WhiteCard>

      {/* ================= COLORANT CONSUMPTION ================= */}
      <WhiteCard>
        <div className={classes.datechartHeader}>
          <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.ColorantConsumption')}</h2>
          <div className={classes.chartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            <ChartButton onClick={() => setOpenColorantRange(!openColorantRange)} style={{ height: 32, paddingTop: 0, paddingBottom: 0, paddingLeft: 10, paddingRight: 10 }}>
              <CalendarIcon className={classes.CalendarIcon} /> {fmtDisplay(colorantStart)} - {fmtDisplay(colorantEnd)}
            </ChartButton>
            <Tooltip title={t('xdataLocationDetails.reload')}>
              <Refresh onClick={fetchColorantConsumption} style={{ padding: 7, marginLeft: 5 }}>
                <AiOutlineReload />
              </Refresh>
            </Tooltip>
            <DownloadButton
              t={t}
              disabled={colorantMerged.series.every((s) => !s.hasData)}
              filename="Colorant Consumption (Combined)"
              data={colorantMerged.sortedDates.map((d) => {
                const row = { date: d };
                colorantMerged.series.forEach((s) => {
                  row[s.code] = s.rows.find((r) => r.date === d)?.litres ?? 0;
                });
                return row;
              })}
            />
          </div>
        </div>
        <DateRangePicker
          open={openColorantRange}
          toggle={() => setOpenColorantRange(!openColorantRange)}
          maxDate={DATE_RANGE_MAX()}
          minDate={DATE_RANGE_MIN()}
          onChange={(range) => {
            const clamped = clampDateRange(range);
            setColorantStart(clamped.startDate);
            setColorantEnd(clamped.endDate);
            setOpenColorantRange(false);
          }}
          wrapperClassName={classes.wrapperClassNameSalesVolume}
        />
        {ccMultiBucketLoading?.colorantConsumption ? (
          <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
        ) : !colorantConsumption.anySuccess && ccMultiBucketError?.colorantConsumption ? (
          <ErrorBlock btnClick={fetchColorantConsumption} />
        ) : colorantMerged.series.every((s) => !s.hasData) ? (
          <SectionPlaceholder>
            <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
            <BucketStatusStrip codeList={codeList} />
          </SectionPlaceholder>
        ) : (
          <BarChart
            chartInfo={{
              tooltip: { trigger: 'axis' },
              legend: { data: colorantMerged.legend, top: 0 },
              // same percentage grid as Dashboard.js's own Colorant
              // Consumption chart (bottom:'13%', containLabel:true)
              grid: { left: '3%', right: '4%', bottom: '13%', top: 40, containLabel: true },
              xAxis: { type: 'category', data: colorantMerged.sortedDates, boundaryGap: false, axisLabel: { showMaxLabel: true } },
              yAxis: { type: 'value', axisLabel: { formatter: '{value}', margin: 10 } },
              dataZoom: DATA_ZOOM_CONFIG,
              color: colorantMerged.series.map((s) => s.color),
              series: colorantMerged.series.map((s) => ({
                name: s.code,
                type: 'line',
                smooth: true,
                data: colorantMerged.sortedDates.map((d) => {
                  const row = s.rows.find((r) => r.date === d);
                  return row ? Number(row.litres || 0) : 0;
                }),
              })),
            }}
          />
        )}
      </WhiteCard>

      {/* ================= COLORANT CONSUMPTION BAR CHART ================= */}
      <WhiteCard>
        <div className={classes.datechartHeader}>
          <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.ColorantConsumptionBarChart')}</h2>
          <div className={classes.chartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            <div className="radioButtonStyle" style={{ display: 'inline-grid' }}>
              <FormControlLabel name="radio-buttons" value={true}
                control={<Radio checked={colorantBarTopTen === true} onChange={() => setColorantBarTopTen(true)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.TopTen')} />
              <FormControlLabel style={{ marginTop: 3 }} name="radio-buttons" value={false}
                control={<Radio checked={colorantBarTopTen === false} onChange={() => setColorantBarTopTen(false)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.WorstTen')} />
            </div>
            <ButtonDropdown
              label={t('XdataAnalyticsDetail.lastyear')}
              options={[
                t('XdataAnalyticsDetail.lastyear'), t('XdataAnalyticsDetail.lastmonth'),
                t('XdataAnalyticsDetail.thisyear'), t('XdataAnalyticsDetail.thismonth'),
              ]}
              selectedOption={colorantBarDateLabel}
              annualSalesTrendSelectOption={colorantBarSelectOption}
            />
            <Tooltip title={t('xdataLocationDetails.reload')}>
              <Refresh onClick={fetchColorantConsumptionBar} style={{ padding: 7, marginLeft: 5 }}>
                <AiOutlineReload />
              </Refresh>
            </Tooltip>
            <DownloadButton
              t={t}
              disabled={colorantConsumptionBarMerged.rows.length === 0}
              filename="Colorant Consumption Bar (Combined)"
              data={colorantConsumptionBarMerged.rows}
            />
          </div>
        </div>
        {ccMultiBucketLoading?.colorantConsumptionBar ? (
          <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
        ) : !colorantConsumptionBar.anySuccess && ccMultiBucketError?.colorantConsumptionBar ? (
          <ErrorBlock btnClick={fetchColorantConsumptionBar} />
        ) : (
          <>
            <BucketChipStrip codeList={codeList} hasDataByCode={colorantConsumptionBarMerged.bucketHasData} />
            {colorantConsumptionBarMerged.rows.length === 0 ? (
              <SectionPlaceholder>
                <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
              </SectionPlaceholder>
            ) : (
              <>
                <BarChart
                  chartInfo={{
                tooltip: { trigger: 'axis' },
                grid: { left: '3%', right: '4%', bottom: '6%', containLabel: true },
                xAxis: { type: 'category', data: colorantConsumptionBarMerged.rows.map((r) => r.colorant) },
                yAxis: { type: 'value' },
                series: [{
                  data: colorantConsumptionBarMerged.rows.map((r) => r.colorant_qnt_agg),
                  type: 'bar',
                  itemStyle: { borderRadius: 5, color: '#C8102E' },
                  barWidth: 13,
                }],
              }}
            />
            <div style={{ display: 'flex', marginLeft: 40, marginTop: 10 }}>
              <BiInfoCircle style={{ fontSize: 14, marginRight: 5 }} />
              <p style={{ fontFamily: 'Roboto', fontSize: 11 }}>{t('XdataAnalyticsDetail.allValuesinLitre')}</p>
            </div>
          </>
            )}
          </>
        )}
      </WhiteCard>

      {/* ================= LOCATION BASED SALES ================= */}
      <WhiteCard>
        <div className={classes.datechartHeader}>
          <h2 className={classes.chartTitle}>{t('XdataAnalyticsDetail.LocationBasedSales')}</h2>
          <div className={classes.datechartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center', width: '50%' }}>
            <div className="radioButtonStyle" style={{ display: 'inline-grid' }}>
              <FormControlLabel name="radio-buttons" value={true}
                control={<Radio checked={locationBasedTopTen === true} onChange={() => setLocationBasedTopTen(true)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.TopTen')} />
              <FormControlLabel style={{ marginTop: 3 }} name="radio-buttons" value={false}
                control={<Radio checked={locationBasedTopTen === false} onChange={() => setLocationBasedTopTen(false)} style={{ color: '#C8102E' }} />}
                label={t('XdataAnalyticsDetail.WorstTen')} />
            </div>
            <Tooltip title={t('XdataAnalyticsDetail.reload')}>
              <ChartButton onClick={fetchAll} style={{ marginRight: 5 }}>
                <AiOutlineReload className={classes.DownloadIcon} />
              </ChartButton>
            </Tooltip>
            <DownloadButton
              t={t}
              disabled={locationBasedMerged.rows.length === 0}
              filename="Location Based Sales (Combined)"
              data={locationBasedMerged.rows}
            />
          </div>
        </div>
        {ccMultiBucketLoading?.locationBasedSales ? (
          <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
        ) : !locationBasedSales.anySuccess && ccMultiBucketError?.locationBasedSales ? (
          <ErrorBlock btnClick={fetchAll} />
        ) : (
          <>
            <BucketChipStrip codeList={codeList} hasDataByCode={locationBasedMerged.bucketHasData} />
            {locationBasedMerged.rows.length === 0 ? (
              <SectionPlaceholder>
                <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
              </SectionPlaceholder>
            ) : (
              <BarChart
                height={430}
                chartInfo={{
                  tooltip: { trigger: 'axis', position: (pt) => [pt[0], '10%'] },
                  grid: { left: '3%', right: '4%', bottom: '9%', containLabel: true },
                  legend: { bottom: -5, right: 0, align: 'left' },
                  xAxis: {
                    type: 'category',
                    data: locationBasedMerged.rows.map((r) => r.pos),
                    axisLabel: { width: locationBasedMerged.rows.length > 5 ? 80 : 100, overflow: 'truncate', interval: 0 },
                    axisTick: { alignWithLabel: true },
                  },
                  yAxis: { type: 'value' },
                  textStyle: { fontFamily: 'Roboto' },
                  series: [
                    {
                      name: t('XdataAnalyticsDetail.prevQuarter'),
                      data: locationBasedMerged.rows.map((r) => r.second_quarter_sales_cnt),
                      type: 'bar',
                      itemStyle: { color: '#FFA1B0', borderRadius: 5 },
                      barWidth: 13,
                    },
                    {
                      name: t('XdataAnalyticsDetail.currentQuarter'),
                      data: locationBasedMerged.rows.map((r) => r.first_quarter_sales_cnt),
                      type: 'bar',
                      itemStyle: { color: '#C8102E', borderRadius: 5 },
                      barWidth: 13,
                    },
                  ],
                }}
              />
            )}
            <div style={{ position: 'absolute', bottom: 26, left: 40, display: 'flex' }}>
              <BiInfoCircle style={{ fontSize: 14, marginRight: 5 }} />
              <p style={{ fontFamily: 'Roboto', fontSize: 11 }}>{t('XdataAnalyticsDetail.prevandcurrentquarter')}</p>
            </div>
          </>
        )}
      </WhiteCard>

      {/* ================= TOP CAN SALES + CUSTOM FORMULA vs REGULAR ================= */}
      <div className={classes.row} style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 500px' }}>
          <WhiteCard>
            <div className={classes.datechartHeader}>
              <h2 className={classes.chartTitleFullWidth}>{t('XdataAnalyticsDetail.TopCanSales')}</h2>
              <div className={classes.chartRightDiv} style={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                <ButtonDropdown
                  label={t('XdataAnalyticsDetail.lastyear')}
                  options={[
                    t('XdataAnalyticsDetail.lastyear'), t('XdataAnalyticsDetail.lastmonth'),
                    t('XdataAnalyticsDetail.thisyear'), t('XdataAnalyticsDetail.thismonth'),
                  ]}
                  selectedOption={topCanSalesDateLabel}
                  annualSalesTrendSelectOption={topCanSalesSelectOption}
                />
                <Tooltip title={t('xdataLocationDetails.reload')}>
                  <Refresh onClick={fetchTopCanSales} style={{ padding: 7, marginLeft: 5 }}>
                    <AiOutlineReload />
                  </Refresh>
                </Tooltip>
                <DownloadButton
                  t={t}
                  disabled={topCanSalesMerged.rows.length === 0}
                  filename="Top Can Sales (Combined)"
                  data={topCanSalesMerged.rows}
                />
              </div>
            </div>
            {ccMultiBucketLoading?.topCanSales ? (
              <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
            ) : !topCanSales.anySuccess && ccMultiBucketError?.topCanSales ? (
              <ErrorBlock btnClick={fetchTopCanSales} />
            ) : (
              <>
                <BucketChipStrip codeList={codeList} hasDataByCode={topCanSalesMerged.bucketHasData} />
                {topCanSalesMerged.rows.length === 0 ? (
                  <SectionPlaceholder>
                    <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
                  </SectionPlaceholder>
                ) : (
                  <BarChart
                    chartInfo={{
                      tooltip: { trigger: 'axis' },
                      grid: { left: '3%', right: '4%', bottom: '6%', containLabel: true },
                      xAxis: { type: 'category', data: topCanSalesMerged.rows.map((r) => r.can_size) },
                      yAxis: { type: 'value' },
                      series: [{ data: topCanSalesMerged.rows.map((r) => r.sale_volume), type: 'bar', itemStyle: { color: '#36333d' }, barWidth: 13 }],
                    }}
                  />
                )}
              </>
            )}
          </WhiteCard>
        </div>

        <div style={{ flex: '1 1 400px' }}>
          <WhiteCard>
            <div className={classes.datechartHeader}>
              <h2 className={classes.chartTitleFullWidth}>{t('XdataAnalyticsDetail.SalesofCustomFormulaevsRegular')}</h2>
              <DownloadButton
                t={t}
                disabled={customFormulaCombined.total === 0}
                filename="Custom Formula vs Regular (Combined)"
                data={[
                  ...customFormulaCombined.perBucketRows,
                  { bucket_id: 'Combined (all selected buckets)', regular: customFormulaCombined.regular, custom_formula: customFormulaCombined.custom, total: customFormulaCombined.total },
                ]}
              />
            </div>
            {ccMultiBucketLoading?.customFormulaSales ? (
              <SectionPlaceholder><CircularProgress color="secondary" sx={{ color: '#C8102E' }} /></SectionPlaceholder>
            ) : !customFormulaSales.anySuccess && ccMultiBucketError?.customFormulaSales ? (
              <ErrorBlock btnClick={fetchAll} />
            ) : (
              <>
                <BucketChipStrip codeList={codeList} hasDataByCode={customFormulaCombined.bucketHasData} />
                {customFormulaCombined.total === 0 ? (
                  <SectionPlaceholder>
                    <NoDataText>{t('xdataLocationDetails.noDataFound')}</NoDataText>
                  </SectionPlaceholder>
                ) : (
              <div style={{ marginTop: 36 }}>
                <PieChart
                  chartInfo={{
                    tooltip: { trigger: 'item' },
                    title: { text: t('xdataLocationDetails.total') + customFormulaCombined.total, left: 'center', top: 'center' },
                    legend: { bottom: 0, right: 0, align: 'left' },
                    series: [{
                      name: '',
                      type: 'pie',
                      radius: ['45%', '70%'],
                      avoidLabelOverlap: false,
                      label: { show: false, position: 'center' },
                      data: [
                        { value: customFormulaCombined.regular, name: t('xdataLocationDetails.regular'), itemStyle: { color: '#C8102E' } },
                        { value: customFormulaCombined.custom, name: t('xdataLocationDetails.custom'), itemStyle: { color: '#36333d' } },
                      ],
                    }],
                  }}
                />
              </div>
                )}
              </>
            )}
          </WhiteCard>
        </div>
      </div>
    </div>
  );
}